import React from "react";
import ModalBaseView from "../../../../../common-deprecated/components/modals/aem/ModalBaseView";
import * as Modal from "../../../../../common-deprecated/styles/v2/toyota/globals/Modal";
import { useBnbSelector } from "../../redux/store";
import { certificationMap } from "../../../../../common-deprecated/constants";
import { getExternalConfigurationObject } from "../../utils/typedUtils";
import type { FinanceModalType } from "../../utils/modalConstants";
import { ExternalPriceCalculator } from "../../utils/classes/ExternalPriceCalculator";
import type { StatusEntryType } from "../../../../../common-deprecated/components/modals/status/StatusModalEntry";
import StatusModalEntry from "../../../../../common-deprecated/components/modals/status/StatusModalEntry";
import { getBnbPriceConfig, getPersonalFinanceInsuranceEnabled } from "../../utils/settings/bnbFinanceSettingUtils";
import {
    checkAemFlexibilityMatrixFinanceOrInsuranceEnabled,
    getAemFlexibilityMatrixConfig,
    showAemPriceExclVAT,
} from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import IconWrapper from "../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon from "../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import type { ModalViewType } from "../../../../../common-deprecated/types/CommonTypes";
import * as StatusStyles from "../../../../../common-deprecated/components/modals/status/styles/StatusStyles";

// Useful tools for build and buy component.
const FinanceModal = (props: ModalViewType<FinanceModalType>): JSX.Element => {
    const { show, close, modalSettings } = props;

    const step = useBnbSelector((state) => state.navigation.step);
    const car = useBnbSelector((state) => state.car);
    const psls = useBnbSelector((state) => state.psls);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const externalConfigurationObject = useBnbSelector((state) =>
        state.car.id
            ? getExternalConfigurationObject(
                  state.commonSettings,
                  state.car,
                  state.basket,
                  state.items,
                  state.tradeInAemExternalData,
                  true,
                  state.finance.quotes,
                  state.utils.financeOption,
              )
            : null,
    );
    const modelCode = useBnbSelector(
        (state) => state.model?.internalCode || state.grade?.model?.code || state.car?.model?.internalCode || "",
    );
    const externalPrice = useBnbSelector((state) => state.externalPrice);

    const { availability } = car;

    const { calculaterInput, calculaterOutput } = ExternalPriceCalculator.getDebugInfo();
    const { personalFinanceEnabled, personalInsuranceEnabled } = getPersonalFinanceInsuranceEnabled(bnbSettings);

    const entries: StatusEntryType[] = [
        {
            title: "Finance toggle flags",
            description: "Finance/cash toggles are shown when all flags below are true.",
            values: [
                {
                    label: "Flexibility matrix: finance enabled",
                    value: checkAemFlexibilityMatrixFinanceOrInsuranceEnabled(
                        "finance",
                        commonSettings,
                        AemFmComponent.Build,
                        "new",
                        modelCode,
                    ),
                },
                {
                    label: "showMonthlyRateCarConfig is true",
                    value: bnbSettings.showMonthlyRateCarConfig,
                },
                {
                    label: "Prices are not ExclVAT",
                    value: !showAemPriceExclVAT(commonSettings, AemFmComponent.Build, "new", modelCode),
                },
                {
                    label: "At least one car has an available monthly rate",
                    value: bnbSettings.showPageMonthlyRate,
                },
            ].concat(
                car
                    ? [
                          {
                              label: "showMonthlyRate is available for current car (only applies to engine selection, customize and summary)",
                              value: !car || car?.availability?.showMonthlyRate,
                          },
                      ]
                    : [],
            ),
        },
        {
            title: "Buy toggle flags",
            description: "Buy step is enabled when all flags below are true.",
            values: [
                { label: "car.availableForOnlineSales is true", value: car.availableForOnlineSales },
                { label: "carconfig.enableBuy is true", value: bnbSettings.enableBuy },
            ],
        },
        {
            title: "WLTP calculation flags",
            description: "WLTP calculation is enabled when all flags below are filled in / true.",
            values: [
                { label: "car.certification", value: certificationMap[car.certification] },
                {
                    label: "priceCalculationService",
                    value: bnbSettings.priceCalculationService,
                },
                { label: "enableWLTPPriceCalculation", value: bnbSettings.enableWLTPPriceCalculation },
            ],
        },
        {
            title: "Tax and costs flags",
            values: [
                {
                    label: "enableBuy",
                    value: bnbSettings.enableBuy,
                },
                {
                    label: "Country (needs to be fr)",
                    value: commonSettings.country,
                },
                {
                    label: "ACE taxFormats",
                    collapsibleContent: JSON.stringify(bnbSettings.taxFormats),
                },
                {
                    label: "externalprice calculation is enabled",
                    value: externalPrice.enabled,
                },
                {
                    label: "externalPrice calculation is working",
                    value: !externalPrice.error,
                },
            ],
        },
        {
            title: "Finance settings",
            values: [
                {
                    label: "Finance Service Gateway",
                    value: bnbSettings.financeGateway,
                    long: true,
                },
                {
                    label: "monthlyRateService",
                    value: bnbSettings.monthlyRateService,
                    long: true,
                },
                {
                    label: "monthlyRateConfigurablesService",
                    value: bnbSettings.monthlyRateConfigurablesService,
                    long: true,
                },
                {
                    label: "monthlyRateDisclaimerService",
                    value: bnbSettings.monthlyRateDisclaimerService,
                    long: true,
                },
                { label: "financeModuleURL", value: bnbSettings.financeModuleURL, long: true },
                {
                    label: "showMonthlyRateCarConfig",
                    value: bnbSettings.showMonthlyRateCarConfig,
                },
                {
                    label: "showAccessoriesRateCarConfig",
                    value: bnbSettings.showAccessoriesRate,
                },
                { label: "sentFinanceStepToyotaCode", value: bnbSettings.sentFinanceStepToyotaCode },
                {
                    label: "personalFinanceEnabled",
                    value: personalFinanceEnabled,
                },
                {
                    label: "disableFinanceSteps",
                    value: bnbSettings.disableFinanceSteps?.join(", ") || "<undefined>",
                },
                {
                    label: "defaultFinanceOption",
                    value: bnbSettings.defaultFinanceOption,
                },
            ],
        },
        {
            title: "Insurance settings",
            values: [
                {
                    label: "monthlyRateService",
                    value: bnbSettings.insuranceRate,
                    long: true,
                },
                {
                    label: "monthlyRateDisclaimerService",
                    value: bnbSettings.insuranceRateDisclaimer,
                    long: true,
                },
                { label: "insuranceModuleURL", value: bnbSettings.insuranceModuleURL, long: true },
                {
                    label: "showInsuranceRateCarConfig",
                    value: bnbSettings.showInsuranceRateCarConfig,
                },
                {
                    label: "personalInsuranceEnabled",
                    value: personalInsuranceEnabled,
                },
                {
                    label: "requireDefaultInsurance",
                    value: bnbSettings.requireDefaultInsurance,
                },
                {
                    label: "Flexibility matrix: insurance enabled",
                    value: checkAemFlexibilityMatrixFinanceOrInsuranceEnabled(
                        "insurance",
                        commonSettings,
                        AemFmComponent.Build,
                        "new",
                        modelCode,
                    ),
                },
            ],
        },
        {
            title: "Monthly rate availability",
            values: availability
                ? [
                      { label: "ShowAccessoriesRateCarConfig", value: availability.showAccessoriesRateCarConfig },
                      { label: "ShowAnnualInterestRate", value: availability.showAnnualInterestRate },
                      { label: "ShowEffectiveInterestRate", value: availability.showEffectiveInterestRate },
                      { label: "ShowFinancialButton", value: availability.showFinanceButton },
                      { label: "ShowInsuranceButton", value: availability.showInsuranceButton },
                      { label: "ShowMonthlyRate", value: availability.showMonthlyRate },
                      { label: "ShowProductName", value: availability.showProductName },
                      { label: "ShowTerm", value: availability.showTerm },
                      { label: "ShowInsurancePremiumCarConfig", value: availability.showInsurancePremiumCarConfig },
                  ]
                : [],
        },
        {
            title: "AEM Flexibility matrix settings",
            values: [
                {
                    label: "component",
                    value: getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Build, "new", modelCode)
                        ?.component as string,
                },
                {
                    label: "last updated",
                    value: getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Build, "new", modelCode)
                        ?.lastModified,
                },
                { label: "model code", value: modelCode },
                {
                    label: "getPriceConfig priceFormat",
                    value: getBnbPriceConfig(commonSettings, modelCode, step).priceFormat,
                },
                {
                    label: "getPriceConfig priceType",
                    value: getBnbPriceConfig(commonSettings, modelCode, step).priceType as string,
                },
                {
                    label: "showPriceExcl",
                    value: showAemPriceExclVAT(commonSettings, AemFmComponent.Build, "new", modelCode),
                },
                {
                    label: "Parsed AEM Flexibility Matrix Config",
                    collapsibleContent: JSON.stringify(
                        getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Build, "new", modelCode),
                        undefined,
                        2,
                    ),
                },
            ],
        },
        {
            title: "PSLS ",
            values: [
                {
                    label: "Configuration object",
                    value: "",
                    collapsibleContent: JSON.stringify(externalConfigurationObject),
                },
                {
                    label: "Add PSLS data to Finance",
                    value: bnbSettings.addPSLSToFinanceConfigurationObject,
                },
                {
                    label: "Add PSLS data to Insurance",
                    value: bnbSettings.addPSLSToInsuranceConfigurationObject,
                },
                {
                    label: "Configuration object with PSLS data",
                    collapsibleContent:
                        externalConfigurationObject && psls && psls.data
                            ? // PSLS property definition must be the same as set in FinanceInsuranceLogic on line 188
                              JSON.stringify({ ...externalConfigurationObject, PSLS: psls.data })
                            : `n/a (addPSLSToFinanceConfigurationObject is ${bnbSettings.addPSLSToFinanceConfigurationObject.toString()})`,
                },
            ],
        },
        {
            title: "Price calculation",
            values: [
                {
                    label: "Sent object / Input",
                    collapsibleContent: calculaterInput,
                },
                {
                    label: "Received object / Output",
                    collapsibleContent: calculaterOutput,
                },
            ],
        },
    ];

    return (
        <ModalBaseView modalIdType={modalSettings.type} onClose={close} show={show} size="md">
            <Modal.Header hasBackground>
                <Modal.Title>Finance info</Modal.Title>
                <Modal.Button type="button" aria-label="Finance info" onClick={close}>
                    <IconWrapper aria-hidden="true">
                        <Icon variant="close" />
                    </IconWrapper>
                </Modal.Button>
            </Modal.Header>
            <StatusStyles.Wrapper>
                {entries.map((entry, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                        <StatusModalEntry entry={entry} />
                    </React.Fragment>
                ))}
            </StatusStyles.Wrapper>
        </ModalBaseView>
    );
};

export default FinanceModal;
